import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import axios from "axios";
import  api, { API_BASE_URL } from "../../utils/api";
import Sidebar from "./Sidebar";
import Toolbar from "./Toolbar";
import StorageStatus from "./StorageStatus";
import FolderGrid from "./FolderGrid";
import FileGrid from "./FileGrid";
import CreateFolderDialog from "./CreateFolderDialog";

const MAX_STORAGE = 5 * 1024 * 1024 * 1024; // 5GB

const FileManager = () => {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState("Files");
  const [storageUsed, setStorageUsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  useEffect(() => {
    fetchFilesAndFolders(currentPath);
  }, [currentPath]);

  const fetchFilesAndFolders = async (path) => {
    setLoading(true);
    try {
      const res = await api.get(`/file-manager?path=${path}`);
      setFolders(res.data.folders);
      setFiles(res.data.files);
      setStorageUsed(res.data.storageUsed);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
  
    const formData = new FormData();
    formData.append("file", file); // only the file, no path in formData
  
    setUploading(true);
    try {
      // pass path in the query string instead of formData
      await api.post(`/uploadfilesmanager?path=${encodeURIComponent(currentPath)}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      fetchFilesAndFolders(currentPath);
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };
  

  const handleCreateFolder = async () => {
    if (!newFolderName) return;
    try {
      await api.post(`/create-folder`, {
        folderPath: `${currentPath}/${newFolderName}`,
      });
      fetchFilesAndFolders(currentPath);
      setFolderDialogOpen(false);
      setNewFolderName("");
    } catch (err) {
      console.error("Folder create error:", err);
    }
  };

  const handleRenameFile = async (oldName, newName) => {
    try {
      await api.put(`/rename-file`, {
        oldPath: `${currentPath}/${oldName}`,
        newPath: `${currentPath}/${newName}`,
      });
      fetchFilesAndFolders(currentPath);
    } catch (err) {
      console.error("Rename file error:", err);
    }
  };

  
  const handleRenameFolder = async (oldName, newName) => {
    try {
      await api.put(`/rename-folder`, {
        oldPath: `${currentPath}/${oldName}`,
        newPath: `${currentPath}/${newName}`,
      });
      fetchFilesAndFolders(currentPath);
    } catch (err) {
      console.error("Rename folder error:", err);
    }
  };
  


  const handleDeleteFile = async (fileName) => {
    if (!window.confirm(`Delete ${fileName}?`)) return;
    try {
      await api.delete(`/deletefilesmanager`, {
        data: { filePath: `${currentPath}/${fileName}` },
      });
      fetchFilesAndFolders(currentPath);
    } catch (err) {
      console.error("Delete file error:", err);
    }
  };

  const handleDeleteFolder = async (folderName) => {
    if (!window.confirm(`Delete folder ${folderName}?`)) return;
    try {
      await api.delete(`/delete-folder`, {
        data: { folderPath: `${currentPath}/${folderName}` },
      });
      fetchFilesAndFolders(currentPath);
    } catch (err) {
      console.error("Delete folder error:", err);
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <Sidebar folders={folders} onNavigate={(folder) => setCurrentPath(`${currentPath}/${folder}`)} />

      <Box sx={{ flex: 1, p: 3 }}>
        <Toolbar
          currentPath={currentPath}
          setCurrentPath={setCurrentPath}
          onUpload={handleUpload}
          onRefresh={() => fetchFilesAndFolders(currentPath)}
          onCreateFolder={() => setFolderDialogOpen(true)}
        />
        <StorageStatus storageUsed={storageUsed} maxStorage={MAX_STORAGE} />
        <FolderGrid
  folders={folders}
  onOpen={setCurrentPath}
  onDelete={handleDeleteFolder}
  onRename={handleRenameFolder}
  currentPath={currentPath}
/>

        <FileGrid
  files={files}
  currentPath={currentPath}
  onDelete={handleDeleteFile}
  onRename={handleRenameFile}
/>
        <CreateFolderDialog
          open={folderDialogOpen}
          onClose={() => setFolderDialogOpen(false)}
          folderName={newFolderName}
          setFolderName={setNewFolderName}
          onCreate={handleCreateFolder}
        />
      </Box>
    </Box>
  );
};

export default FileManager;
